/** Mock fixtures for the Macro & Markets page.
 *
 * Illustrative layout only. Nothing here reads a provider, a backend route or
 * Mongo. Top-level key names mirror the existing dashboard macro snapshot
 * (vix, vix9d, ratio, dspx, vvix, skew, move, us2y, us10y, spread_2s10s, rv10,
 * vix_rv10, state, interpretation, alerts, matrix) so the shape stays familiar.
 */

function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function monthlySeries({ seed, points, start, drift, vol, digits = 1 }) {
  const rng = mulberry32(seed);
  const out = [];
  let v = start;
  const d = new Date(Date.UTC(2026, 8, 1));
  d.setUTCMonth(d.getUTCMonth() - (points - 1));
  for (let i = 0; i < points; i += 1) {
    v = Math.max(0, v + drift + (rng() - 0.5) * 2 * vol);
    out.push({ date: d.toISOString().slice(0, 7), value: +v.toFixed(digits) });
    d.setUTCMonth(d.getUTCMonth() + 1);
  }
  return out;
}

function dailySeries({ seed, points, start, drift, vol, digits = 2 }) {
  const rng = mulberry32(seed);
  const out = [];
  let v = start;
  const d = new Date(Date.UTC(2026, 8, 4));
  d.setUTCDate(d.getUTCDate() - points);
  for (let i = 0; i < points; i += 1) {
    d.setUTCDate(d.getUTCDate() + 1);
    if (d.getUTCDay() === 0 || d.getUTCDay() === 6) continue;
    v = Math.max(0.1, v + drift + (rng() - 0.5) * 2 * vol);
    out.push({ date: d.toISOString().slice(0, 10), value: +v.toFixed(digits) });
  }
  return out;
}

export const MACRO = {
  asOf: "2026-09-04",

  /* ------------------------------------------------------------ risk regime */
  vix: 17.4,
  vix9d: 16.1,
  ratio: 0.925,
  vvix: 96.2,
  skew: 142.8,
  dspx: 28.6,
  move: 91.4,
  rv10: 12.8,
  vix_rv10: 4.6,
  vixPctile6mo: 58,
  dspxPctile6mo: 71,
  state: "Contained — term structure in contango, dispersion above its six-month median",
  interpretation:
    "Index volatility is unremarkable but single-name dispersion sits in the upper third of its six-month range, so index-level calm is understating what is happening underneath it. Bond volatility is subdued and the curve is positively sloped.",
  alerts: [
    { severity: "caution", label: "Elevated single-name dispersion", detail: "DSPX at the 71st percentile of the last six months." },
    { severity: "info", label: "Tail-risk demand normal", detail: "SKEW near its trailing median." },
  ],
  matrix: [
    { category: "Core volatility", indicator: "VIX · 30-day implied", value: "17.40", pctile: 58, direction: "up" },
    { category: "Core volatility", indicator: "VIX9D / VIX · term structure", value: "0.925", pctile: 34, direction: "down" },
    { category: "Dispersion", indicator: "DSPX · S&P dispersion", value: "28.60", pctile: 71, direction: "up" },
    { category: "Tail risk", indicator: "VVIX · volatility of volatility", value: "96.20", pctile: 44, direction: "down" },
    { category: "Tail risk", indicator: "SKEW · tail-hedge demand", value: "142.80", pctile: 51, direction: "up" },
    { category: "Cross-asset", indicator: "MOVE · bond volatility", value: "91.40", pctile: 29, direction: "down" },
    { category: "Cross-asset", indicator: "US 2s10s spread", value: "+0.54", pctile: 66, direction: "up" },
    { category: "Execution", indicator: "VIX − RV10 · variance premium", value: "+4.60", pctile: 62, direction: "up" },
  ],
  vixHistory: dailySeries({ seed: 5, points: 180, start: 15.2, drift: 0.012, vol: 0.9 }),
  vixTerm: [
    { tenor: "9d", value: 16.1 },
    { tenor: "30d", value: 17.4 },
    { tenor: "60d", value: 18.2 },
    { tenor: "90d", value: 18.9 },
    { tenor: "180d", value: 19.6 },
  ],
  equityBondVol: dailySeries({ seed: 9, points: 180, start: 90, drift: 0.01, vol: 1.6 })
    .map((r, i) => ({ date: r.date, move: r.value, vix: +(14 + Math.sin(i / 9) * 3 + (i % 7) * 0.15).toFixed(2) })),

  /* ------------------------------------------------------ rates and curve */
  us2y: 3.62,
  us10y: 4.16,
  spread_2s10s: 0.54,
  curveState: "positively sloped",
  curve: [
    { tenor: "1M", yield: 3.94, prior: 4.02 },
    { tenor: "3M", yield: 3.88, prior: 3.97 },
    { tenor: "6M", yield: 3.76, prior: 3.85 },
    { tenor: "1Y", yield: 3.68, prior: 3.74 },
    { tenor: "2Y", yield: 3.62, prior: 3.66 },
    { tenor: "5Y", yield: 3.79, prior: 3.80 },
    { tenor: "10Y", yield: 4.16, prior: 4.11 },
    { tenor: "30Y", yield: 4.68, prior: 4.60 },
  ],
  spreadHistory: dailySeries({ seed: 13, points: 180, start: 0.18, drift: 0.002, vol: 0.035 }),

  /* ------------------------------------------ inflation, growth, labour, IP */
  inflation: [
    { name: "CPI · year over year", value: "2.6%", change: -0.1, series: monthlySeries({ seed: 21, points: 24, start: 3.4, drift: -0.035, vol: 0.09 }) },
    { name: "Core CPI · year over year", value: "3.0%", change: -0.1, series: monthlySeries({ seed: 22, points: 24, start: 3.8, drift: -0.032, vol: 0.07 }) },
    { name: "Core PCE · year over year", value: "2.7%", change: 0.0, series: monthlySeries({ seed: 23, points: 24, start: 3.1, drift: -0.018, vol: 0.06 }) },
  ],
  gdp: [
    { period: "2025 Q3", value: 2.6 },
    { period: "2025 Q4", value: 2.1 },
    { period: "2026 Q1", value: 1.8 },
    { period: "2026 Q2", value: 2.3 },
  ],
  labor: [
    { name: "Nonfarm payrolls", value: "+142k", change: -18, unit: "k vs prior", direction: "down" },
    { name: "Unemployment rate", value: "4.2%", change: 0.1, unit: "pp", direction: "up" },
    { name: "Initial claims · 4wk avg", value: "228k", change: -4, unit: "k", direction: "down" },
    { name: "Average hourly earnings", value: "+3.8%", change: -0.1, unit: "pp y/y", direction: "down" },
  ],
  laborSeries: monthlySeries({ seed: 31, points: 24, start: 195, drift: -2.2, vol: 22, digits: 0 }),
  industrial: [
    { name: "Industrial production · y/y", value: "+0.9%", change: 0.2, direction: "up" },
    { name: "Capacity utilisation", value: "77.8%", change: -0.2, direction: "down" },
    { name: "ISM manufacturing", value: "48.6", change: 0.7, direction: "up" },
    { name: "ISM services", value: "53.1", change: -0.4, direction: "down" },
  ],

  /* ------------------------------------------------------ consumer, housing */
  consumer: [
    { name: "Consumer sentiment", value: "68.4", change: 1.9, direction: "up" },
    { name: "Retail sales · m/m", value: "+0.4%", change: 0.2, direction: "up" },
    { name: "Personal savings rate", value: "4.6%", change: -0.2, direction: "down" },
    { name: "Revolving credit · y/y", value: "+5.1%", change: 0.3, direction: "up" },
  ],
  housing: [
    { name: "Housing starts", value: "1.34M", change: -0.03, direction: "down" },
    { name: "Building permits", value: "1.41M", change: 0.02, direction: "up" },
    { name: "30-year mortgage", value: "6.24%", change: -0.08, direction: "down" },
    { name: "Existing-home sales", value: "4.02M", change: 0.05, direction: "up" },
  ],
  housingSeries: monthlySeries({ seed: 41, points: 24, start: 1.42, drift: -0.004, vol: 0.05, digits: 2 }),

  /* -------------------------------------------------------------- WTI oil */
  wti: dailySeries({ seed: 51, points: 180, start: 71.4, drift: 0.018, vol: 1.4 }),
  wtiLast: 74.86,
  wtiChangePct: -1.24,
  wtiContext: {
    last: 74.86,
    changePct: -1.24,
    range20dLow: 71.18,
    range20dHigh: 78.42,
    sessions: 20,
  },

  /* ------------------------------------------------- sectors and breadth */
  sectors: [
    { sector: "Information Technology", ticker: "XLK", changePct: 0.82, rsMomentum: 104.2 },
    { sector: "Communication Services", ticker: "XLC", changePct: 0.61, rsMomentum: 102.8 },
    { sector: "Financials", ticker: "XLF", changePct: 0.34, rsMomentum: 101.1 },
    { sector: "Industrials", ticker: "XLI", changePct: 0.12, rsMomentum: 100.3 },
    { sector: "Health Care", ticker: "XLV", changePct: -0.18, rsMomentum: 98.6 },
    { sector: "Consumer Discretionary", ticker: "XLY", changePct: -0.29, rsMomentum: 99.2 },
    { sector: "Consumer Staples", ticker: "XLP", changePct: -0.41, rsMomentum: 97.4 },
    { sector: "Energy", ticker: "XLE", changePct: -0.88, rsMomentum: 95.8 },
    { sector: "Materials", ticker: "XLB", changePct: -0.52, rsMomentum: 97.9 },
    { sector: "Utilities", ticker: "XLU", changePct: 0.24, rsMomentum: 100.9 },
    { sector: "Real Estate", ticker: "XLRE", changePct: -0.66, rsMomentum: 96.7 },
  ],
  breadth: [
    { name: "Advancers vs decliners", value: "241 / 259", ratio: 0.48 },
    { name: "Above 50-day average", value: "54%", ratio: 0.54 },
    { name: "Above 200-day average", value: "61%", ratio: 0.61 },
    { name: "New 52-week highs", value: "38", ratio: 0.62 },
    { name: "New 52-week lows", value: "14", ratio: 0.28 },
  ],
  advanceDecline: dailySeries({ seed: 61, points: 120, start: 0, drift: 0.4, vol: 26, digits: 0 }),

  /* ------------------------------------------------------- market calendar */
  calendar: [
    { date: "2026-09-08", event: "Consumer credit", importance: "low", domain: "Consumer" },
    { date: "2026-09-10", event: "CPI · August", importance: "high", domain: "Inflation" },
    { date: "2026-09-11", event: "Initial jobless claims", importance: "medium", domain: "Labour" },
    { date: "2026-09-12", event: "PPI · August", importance: "medium", domain: "Inflation" },
    { date: "2026-09-16", event: "Retail sales · August", importance: "high", domain: "Consumer" },
    { date: "2026-09-17", event: "FOMC rate decision", importance: "high", domain: "Rates" },
    { date: "2026-09-18", event: "Philadelphia Fed index", importance: "low", domain: "Industry" },
    { date: "2026-09-18", event: "Quarterly options expiration", importance: "medium", domain: "Markets" },
  ],
  equityBondHeadline: "VIX 17.4 versus MOVE 91.4 — equity implied vol is quieter than bond vol in this snapshot.",
};

/** Compact sector heatmap tiles, shaped for the existing SectorHeatmapGrid. */
export const SECTOR_HEATMAP = MACRO.sectors.map((s) => ({
  sector: s.sector,
  score: +(s.changePct / 1.5).toFixed(2),
  tone: s.changePct > 0.3 ? "Leading" : s.changePct < -0.4 ? "Lagging" : "In line",
  timestamp: MACRO.asOf,
  etf: s.ticker,
  rsMomentum: s.rsMomentum,
}));
